import React, { useEffect, useRef, useState } from 'react';
import { Text } from 'react-konva';
import { Html } from 'react-konva-utils';
import { useTemplateStore } from '../hooks/useTemplateStore';
import { TextElement } from '../../core/template.schemas';
import { resolveTextContent } from '../../core/runtime/resolve';

/**
 * Props for the EditableText component
 */
interface EditableTextProps {
  /** The text element to render and edit */
  element: TextElement;
  /** Callback function when text is updated */
  onUpdate: (id: string, updates: Partial<TextElement>) => void;
  /** Additional Konva Text props */
  [key: string]: any;
}

/**
 * A dedicated component for handling inline text editing.
 * It swaps between a Konva.Text and an HTML textarea.
 */
export const EditableText: React.FC<EditableTextProps> = ({ onUpdate, element, ...props }) => {
  const [isEditing, setIsEditing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const textNodeRef = useRef<any>(null);
  const selectElement = useTemplateStore(state => state.selectElement);
  
  // Resolve the text content using the current context
  const resolveContext = { selected: {}, overrides: {} }; // TODO: Get actual context from store
  const resolvedText = resolveTextContent(element.content, resolveContext);

  // Check if the content is dynamic (editing should be disabled for dynamic content)
  const isDynamicContent = element.content.mode === 'dynamic';

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
      // Deselect element when editing starts to prevent transformer conflicts
      selectElement(null);
    }
  }, [isEditing, selectElement]);

  // Caching effect for filters (Blur)
  useEffect(() => {
    const node = textNodeRef.current;
    if (node) {
      if (props.filters && props.filters.length > 0) {
        node.cache();
      } else {
        node.clearCache();
      }
    }
  }, [props.filters, props.blurRadius, props.shadowColor, props.width, props.height, resolvedText]);

  const finishEditing = () => {
    if (textareaRef.current && textareaRef.current.value !== resolvedText) {
      // Update the content field with new static value
      onUpdate(element.id, { 
        content: {
          mode: 'static',
          mediaType: 'text',
          staticValue: textareaRef.current.value
        }
      });
    }
    setIsEditing(false);
    // Re-select element after editing
    selectElement(element.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      finishEditing();
    }
    if (e.key === 'Escape') {
      // Restore original text on escape
      if (textareaRef.current) {
        textareaRef.current.value = resolvedText || '';
      }
      setIsEditing(false);
      // Re-select element after canceling
      selectElement(element.id);
    }
  };

  const handleTextClick = () => {
    // Only allow editing if content is static
    if (!isDynamicContent) {
      setIsEditing(true);
    }
  };

  if (isEditing) {
    // When editing, we need to use the top-left coordinates for the HTML textarea,
    // not the center coordinates used by Konva for transformations.
    const topLeftX = props.x - props.offsetX;
    const topLeftY = props.y - props.offsetY;

    return (
      <Html groupProps={{ x: topLeftX, y: topLeftY }} divProps={{ style: { opacity: 1 } }}>
        <div
          style={{
            display: 'flex',
            alignItems:
              element.align.v === 'center'
                ? 'center'
                : element.align.v === 'bottom'
                ? 'flex-end'
                : 'flex-start',
            width: `${props.width}px`,
            height: `${props.height}px`,
          }}
        >
          <textarea
            ref={textareaRef}
            defaultValue={resolvedText || ''}
            onBlur={finishEditing}
            onKeyDown={handleKeyDown}
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              fontSize: `${props.fontSize}px`,
              fontFamily: props.fontFamily,
              textAlign: props.align,
              color: props.fill,
              lineHeight: props.lineHeight,
              padding: `${props.padding}px`,
              margin: 0,
              border: '1.8px solid #3b82f6',
              outline: 'none',
              overflow: 'hidden',
              background: 'transparent',
              resize: 'none',
              whiteSpace: 'pre-wrap',
            }}
          />
        </div>
      </Html>
    );
  }

  return (
    <Text 
      ref={textNodeRef}
      {...props} 
      onDblClick={handleTextClick} 
      onDblTap={handleTextClick}
      // Visual indicator for dynamic content (optional: slightly different cursor)
      style={isDynamicContent ? { cursor: 'default' } : { cursor: 'text' }}
    />
  );
};